import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import { closeModal } from "store/modals/slice";
import {
  handleClearFields,
  getPage,
  createPage,
  updatePage,
} from "store/cms/pages/list/slice";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import { ILocaleData, ModalPageTabs } from "configs/types";

import Button from "components/buttons/Button";
import PagesMainTab from "./tabs/PagesMainTab";

import styles from "../list/CmsPagesEditModal.module.scss";

import LocalTab from "../../globalTabs/LocalTab";
import instance from "../../../../api/apiClient";
import { apiRoutes } from "../../../../configs/apiRoutes";
import { Modal } from "components/Modal/Modal";
import PagesTrailTab from "./tabs/PagesTrailTab";
import { useFormErrors } from "helpers/useFormErrors";
import { handleApiError } from "helpers/handleApiError";
import ModalSkeleton, {
  ModalFooterSkeleton,
} from "components/global/Skeleton/ModalSkeleton/ModalSkeleton";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import {
  getChangedFields,
  normalizeOptionalText,
} from "helpers/getChangedFields";
import { normalizeSeoHtml } from "helpers/seoAttributes";

const CmsPagesEditModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const isItemFetched = useAppSelector(
    (store) => store["cms-pages"].isItemFetched,
  );
  const payload = useAppSelector((store) => store["cms-pages"].payload);
  const isItemFetching = useAppSelector(
    (store) => store["cms-pages"].isItemFetching,
  );

  const formErrors = useFormErrors();
  const entityId = searchParams.get("entityId");
  const isEdit = Boolean(entityId);

  const [tabs] = useState<ModalPageTabs[]>([
    ModalPageTabs.main,
    ModalPageTabs.local,
    ModalPageTabs.trail,
    // ModalPageTabs.settings,
  ]);
  const [activeTab, setActiveTab] = useState<ModalPageTabs>(ModalPageTabs.main);
  const [localeData, setLocaleData] = useState<ILocaleData>();
  const [initialLocaleData, setInitialLocaleData] = useState<ILocaleData>();
  const [localeReady, setLocaleReady] = useState(false);
  const [domainOptions, setDomainOptions] = useState([
    { value: "", label: "Default / Brand domain" },
  ]);

  useEffect(() => {
    if (activeTab !== ModalPageTabs.local) return;

    let cancelled = false;
    instance
      .post(apiRoutes.mirrorList)
      .then((result) => {
        if (cancelled) return;
        const mirrors = result.data?.items ?? [];
        setDomainOptions([
          { value: "", label: "Default / Brand domain" },
          ...mirrors
            .filter(
              (item: { status: string; domain: string }) =>
                ["created", "activated"].includes(item.status) && item.domain,
            )
            .map((item: { domain: string; isDefault: number }) => ({
              value: item.domain,
              label: `${item.domain}${item.isDefault === 1 ? " (Main)" : ""}`,
            })),
        ]);
      })
      .catch((error) => {
        if (!cancelled) handleApiError({ error, t, dispatch });
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab, entityId, dispatch, t]);

  const permissions = usePermissionHelper();
  const canSave = isEdit
    ? Boolean(permissions?.update)
    : Boolean(permissions?.create);

  useEffect(() => {
    if (searchParams.get("entityId") !== null && !isItemFetched) {
      dispatch(getPage(searchParams.get("entityId") ?? ""));
    }
  }, [searchParams, isItemFetched]);

  useEffect(() => {
    formErrors.resetErrors();
  }, [activeTab]);

  const isLoaded = () => {
    if (searchParams.get("entityId") !== null && !isItemFetched) {
      return false;
    }

    return true;
  };

  function stripHTML(html: string) {
    const text = html.replace(/<[^>]*>/g, "");
    return text.trim();
  }

  const handleCloseClick = () => {
    navigate(`${location.pathname}`);
    dispatch(closeModal());
    dispatch(handleClearFields());
  };

  const handleApply = async () => {
    if (!canSave) {
      return;
    }

    if (payload && activeTab === ModalPageTabs.main) {
      if (!isEdit) {
        formErrors.resetErrors();
      }

      try {
        if (isEdit) {
          await dispatch(updatePage(payload)).unwrap();
        } else {
          const newId = await dispatch(createPage(payload)).unwrap();

          navigate(`${location.pathname}?entityId=${newId}`);
        }

        dispatch(
          showNotification({
            title: t("success"),
            message: t(isEdit ? "entityUpdateSuccess" : "entityCreateSuccess"),
            type: NotificationTypes.success,
          }),
        );
      } catch (error) {
        handleApiError({
          error,
          t,
          dispatch,
          formErrors,
        });
      }

      return;
    }

    if (activeTab === ModalPageTabs.local) {
      await handleSetLocale();
    }
  };

  const handleSetLocale = async () => {
    if (!localeData || !localeReady) return;

    const { geo, locale, domain, isActive } = localeData;
    const textChanges = getChangedFields(
      initialLocaleData,
      localeData,
      ["title", "content"],
      {
        title: normalizeOptionalText,
        content: normalizeSeoHtml,
      },
    );

    try {
      await instance.put(`${apiRoutes.setPagesItemLocale}/${entityId}`, {
        geo,
        locale,
        ...(domain ? { domain } : {}),
        ...textChanges,
        isActive: isActive ? 1 : 0,
      });
      setInitialLocaleData(localeData);

      dispatch(
        showNotification({
          title: t("success"),
          message: t("entityUpdateSuccess"),
          type: NotificationTypes.success,
        }),
      );
    } catch (error: any) {
      handleApiError({
        error,
        t,
        dispatch,
        formErrors,
      });
    }
  };

  const handleTabClick = (tab: ModalPageTabs) => {
    if (!isEdit && tab !== ModalPageTabs.main) {
      dispatch(
        showNotification({
          title: t("warning"),
          message: t("createEntityFirst"),
          type: NotificationTypes.warning,
        }),
      );
      return;
    }

    setActiveTab(tab);
    if (tab !== activeTab) setLocaleReady(false);
  };

  const renderTab = () => {
    if (activeTab === ModalPageTabs.main) {
      return (
        <PagesMainTab
          fieldErrors={formErrors.errors}
          clearFieldError={formErrors.clearFieldError}
          readOnly={!canSave}
        />
      );
    }
    if (activeTab === ModalPageTabs.local) {
      return (
        <LocalTab
          id={searchParams.get("entityId") ?? undefined}
          currentData={localeData}
          handleData={setLocaleData}
          onInitialData={setInitialLocaleData}
          domainOptions={domainOptions}
          onReadyChange={setLocaleReady}
          onLoadError={(error) => handleApiError({ error, t, dispatch })}
          isAiAssist={!!stripHTML(payload?.content ?? "")}
          url={apiRoutes.getPagesItemLocale}
          type="page"
          fieldErrors={formErrors.errors}
          clearFieldError={formErrors.clearFieldError}
          resetErrors={formErrors.resetErrors}
          readOnly={!canSave}
        />
      );
    }
    if (activeTab === ModalPageTabs.trail) {
      return <PagesTrailTab entityId={searchParams.get("entityId")!} />;
    }
  };

  return (
    <Modal onClose={handleCloseClick}>
      <Modal.Header>{t("Page")}</Modal.Header>

      {!isLoaded() ? (
        <ModalSkeleton />
      ) : (
        <>
          <Modal.Tabs
            tabs={tabs}
            activeTab={activeTab}
            onClick={handleTabClick}
            canBeClicked={isItemFetched}
          />

          <Modal.Content
            className={`${styles.cmsPagesContentWrapper} ${
              activeTab === ModalPageTabs.trail
                ? styles.trailContentWrapper
                : ""
            }`}
          >
            <div className="pages-content">{renderTab()}</div>
          </Modal.Content>

          {activeTab !== ModalPageTabs.trail && canSave && (
            <Modal.Footer>
              {activeTab === ModalPageTabs.local && !localeReady ? (
                <ModalFooterSkeleton />
              ) : (
                <>
                  <Button
                    isSecondary
                    txt={t("cancel")}
                    handleClick={handleCloseClick}
                  />
                  <Button
                    isPrimary
                    isLoad={isItemFetching}
                    disabled={activeTab === ModalPageTabs.local && !localeReady}
                    isSecondary
                    handleClick={handleApply}
                    txt={t("save")}
                  />
                </>
              )}
            </Modal.Footer>
          )}
        </>
      )}
    </Modal>
  );
};

export default CmsPagesEditModal;
